import React from 'react';
import { getStatusLabel } from '../../common/StatusDropdown';

const TaskActivitySection = ({
    formatRelativeTime,
    createdDate,
    creatorName,
    createdTimeAgo,
    creator,
    taskActivities,
}) => {
    const formatActivityMessage = (activity) => {
        const actorName = activity.actor?.display_name || activity.actor?.name || 'User';
        const action = activity.action || '';
        const meta = activity.meta || {};
        
        // Handle different activity types
        switch (action) {
            case 'create_task':
                return (
                    <>
                        <strong>{actorName}</strong> created this task
                        {meta.task_title && <span className="activity-meta"> "{meta.task_title}"</span>}
                    </>
                );
            
            case 'update_task_title':
                return (
                    <>
                        <strong>{actorName}</strong> renamed this task
                        {meta.task_title_old && meta.task_title && (
                            <span className="activity-meta">
                                {' '}from "{meta.task_title_old}" to "{meta.task_title}"
                            </span>
                        )}
                    </>
                );
            
            case 'update_task_status':
                const statusOld = getStatusLabel(meta.task_status_old ?? 0);
                const statusNew = getStatusLabel(meta.task_status ?? 0);
                return (
                    <>
                        <strong>{actorName}</strong> changed status
                        {meta.task_status_old !== undefined && meta.task_status !== undefined && (
                            <span className="activity-meta">
                                {' '}from <strong>{statusOld}</strong> to <strong>{statusNew}</strong>
                            </span>
                        )}
                    </>
                );
            
            case 'update_task_due_date':
                const dueDate = meta.task_due_date 
                    ? new Date(meta.task_due_date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                    })
                    : 'removed';
                return (
                    <>
                        <strong>{actorName}</strong> {meta.task_due_date ? 'set' : 'removed'} the due date
                        {meta.task_due_date && <span className="activity-meta"> to {dueDate}</span>}
                    </>
                );
            
            case 'update_task_description':
                return (
                    <>
                        <strong>{actorName}</strong> updated the description
                    </>
                );
            
            case 'update_task_priority':
                const priorityLabels = {
                    0: 'low',
                    1: 'normal',
                    2: 'high',
                    3: 'urgent'
                };
                const priority = priorityLabels[meta.task_priority] || meta.task_priority;
                return (
                    <>
                        <strong>{actorName}</strong> changed priority to <strong>{priority}</strong>
                    </>
                );
            
            case 'update_task_recurrent':
            case 'disable_task_recurring':
                return (
                    <>
                        <strong>{actorName}</strong> {action === 'disable_task_recurring' ? 'disabled' : 'updated'} recurring settings
                    </>
                );
            
            case 'delete_task':
                return (
                    <>
                        <strong>{actorName}</strong> deleted this task
                        {meta.deleted_task_title && <span className="activity-meta"> "{meta.deleted_task_title}"</span>}
                    </>
                );
            
            case 'comment_on_task':
                return (
                    <>
                        <strong>{actorName}</strong> commented on this task
                    </>
                );
            
            case 'add_task_assignee':
                return (
                    <>
                        <strong>{actorName}</strong> assigned <strong>{meta.assignee_name || 'someone'}</strong> to this task
                    </>
                );
            
            case 'remove_task_assignee':
                return (
                    <>
                        <strong>{actorName}</strong> unassigned <strong>{meta.assignee_name || 'someone'}</strong> from this task
                    </>
                );
            
            case 'create_task_list':
                return (
                    <>
                        <strong>{actorName}</strong> created task list
                        {meta.task_list_title && <span className="activity-meta"> "{meta.task_list_title}"</span>}
                    </>
                );
            
            case 'update_task_list':
                return (
                    <>
                        <strong>{actorName}</strong> updated task list
                        {meta.task_list_title && <span className="activity-meta"> "{meta.task_list_title}"</span>}
                    </>
                );
            
            case 'delete_task_list':
                return (
                    <>
                        <strong>{actorName}</strong> deleted task list
                        {meta.deleted_task_list_title && <span className="activity-meta"> "{meta.deleted_task_list_title}"</span>}
                    </>
                );
            
            default:
                // Fallback for unknown actions
                const actionText = action
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
                return (
                    <>
                        <strong>{actorName}</strong> {actionText.toLowerCase()}
                    </>
                );
        }
    };

    return (
        <>
            <div className="task-detail-comments-list">
                {taskActivities && taskActivities.length > 0 ? (
                    taskActivities.map((activity) => (
                        <div key={activity.id} className="task-detail-comment-item">
                            <div className="task-detail-comment-avatar">
                                {activity.actor?.image || activity.actor?.avatar_url ? (
                                    <img
                                        src={
                                            activity.actor.image ||
                                            activity.actor.avatar_url
                                        }
                                        alt={
                                            activity.actor.display_name ||
                                            activity.actor.name
                                        }
                                    />
                                ) : (
                                    <div className="task-detail-avatar-placeholder">
                                        {(
                                            activity.actor?.display_name ||
                                            activity.actor?.name ||
                                            'U'
                                        )
                                            .charAt(0)
                                            .toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="task-detail-comment-content">
                                <div className="task-detail-comment-text">
                                    {formatActivityMessage(activity)}
                                </div>
                                <div className="task-detail-comment-time">
                                    {formatRelativeTime(activity.created_at) && (
                                        <span>
                                            · {formatRelativeTime(activity.created_at)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="task-detail-no-comments">No activity yet</div>
                )}
            </div>
        </>
    );
};

export default TaskActivitySection;


